<template>
    <v-container class="pa-0 pb-5">
        <div v-if="countdown" class="ml-md-8 mr-md-8">
            <v-card flat
                    tile
                    class="pa-4 mb-3 text-center">
                <div class="headline text-uppercase font-weight-regular">
                    {{ $vuetify.lang.t('$vuetify.countdown.prefix') }}
                </div>
                <div class="font-weight-bold" style="font-size: 70px; line-height: 1">
                    {{ formattedCountdown }}
                </div>
                <div class="headline text-uppercase font-weight-regular">
                    <template v-if="formattedCountdown === 1">
                        {{ $vuetify.lang.t('$vuetify.countdown.day') }}
                    </template>
                    <template v-else>
                        {{ $vuetify.lang.t('$vuetify.countdown.days') }}
                    </template>
                </div>
                <p v-if="formattedCountdown > 0" class="mt-2 mb-0 body-1 text-center ml-8 mr-8" v-html="$vuetify.lang.t('$vuetify.countdown.hint')"></p>
                <p v-else class="mt-2 mb-0 body-1 text-center ml-8 mr-8" v-html="$vuetify.lang.t('$vuetify.countdown.hint_final')"></p>
            </v-card>
        </div>
        <div class="ml-8 mr-8">

            <v-expand-transition>
                <div v-show="!showLoginForm && platform === 'web'">
                    <v-row>
                        <v-col cols="6">
                            <a :href="appstoreLink" target="_blank">
                                <v-img src="../../assets/images/appstore.svg"
                                       aspect-ratio="3.3" max-height="50px" contain position="left"></v-img>
                            </a>
                        </v-col>
                        <v-col cols="6">
                            <a :href="playstoreLink" target="_blank">
                                <v-img src="../../assets/images/playstore.svg"
                                       aspect-ratio="3.3" max-height="50px" contain position="right"></v-img>
                            </a>
                        </v-col>
                    </v-row>
                </div>
            </v-expand-transition>

            <v-form v-if="!countdown"
                    ref="form"
                    v-model="valid"
                    lazy-validation>

                <v-expand-transition>
                    <div v-if="showLoginForm && showTwoFactor">
                        <h2 class="title white--text mb-4">
                            {{ $vuetify.lang.t('$vuetify.login.twofactor') }}
                        </h2>

                        <p class="white--text mb-4">
                            {{ $vuetify.lang.t('$vuetify.login.twofactor_description') }}
                        </p>

                        <error-list :errors="errors"></error-list>

                        <v-text-field name="twofactor"
                                      :label="$vuetify.lang.t('$vuetify.form_items.twofcator')"
                                      v-model="form.twofactor"
                                      filled
                                      dense
                                      :disabled="whileSubmitting"
                                      required></v-text-field>
                    </div>
                    <div v-else-if="showLoginForm && !showTwoFactor">
                        <h2 class="title white--text mb-4">
                            {{ $vuetify.lang.t('$vuetify.login.welcome') }}
                        </h2>

                        <error-list :errors="errors"></error-list>

                        <v-text-field name="email"
                                      :label="$vuetify.lang.t('$vuetify.form_items.email')"
                                      :rules="rules.email"
                                      v-model="form.email"
                                      filled
                                      dense
                                      :disabled="whileSubmitting || whileSubmittingTwoFactor"
                                      required></v-text-field>

                        <v-text-field name="password"
                                      v-model="form.password"
                                      :label="$vuetify.lang.t('$vuetify.form_items.password')"
                                      :hint="$vuetify.lang.t('$vuetify.form_item_hints.password_min_chars', [ passwordLength ])"
                                      counter
                                      filled
                                      dense
                                      :rules="rules.password"
                                      :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                      :type="showPassword ? 'text' : 'password'"
                                      :disabled="whileSubmitting || whileSubmittingTwoFactor"
                                      autocomplete="off"
                                      @click:append="showPassword = !showPassword"></v-text-field>

                    </div>
                </v-expand-transition>

                <v-btn @click.prevent="!showLoginForm ? openLoginForm() : submit()"
                       block
                       class="mb-2"
                       :loading="whileSubmitting || whileSubmittingTwoFactor"
                       :disabled="whileSubmitting || whileSubmittingTwoFactor || showLoginForm && !valid"
                       color="primary darken-1">
                    {{ $vuetify.lang.t('$vuetify.buttons.login') }}
                </v-btn>


                <v-expand-transition>
                    <v-btn v-if="showTwoFactor"
                           @click.prevent="submitNewCode"
                           class="mb-2 btn-ghost"
                           block
                           outlined
                           :loading="whileSendingTwoFactor"
                           :disabled="whileSendingTwoFactor || whileSubmittingTwoFactor"
                           color="white">
                        {{ $vuetify.lang.t('$vuetify.buttons.resend_code') }}
                    </v-btn>
                </v-expand-transition>
            </v-form>
            <v-expand-transition>
                <div v-show="showLoginForm">
                    <v-btn small
                           text
                           color="primary"
                           :to="{ name: 'reset-password' }">
                        {{ $vuetify.lang.t('$vuetify.buttons.forgot_password') }}
                    </v-btn>
                </div>
            </v-expand-transition>

            <v-expand-transition>
                <div v-show="!showLoginForm">
                    <v-btn class="mb-2 btn-ghost"
                           block
                           to="/register-participant"
                           outlined
                           color="white">
                        {{ $vuetify.lang.t('$vuetify.buttons.register_as_participant') }}
                    </v-btn>
                    <v-btn block
                           class="btn-ghost"
                           to="/register-company"
                           outlined
                           color="white">
                        {{ $vuetify.lang.t('$vuetify.buttons.register_company') }}
                    </v-btn>
                </div>
            </v-expand-transition>

            <div class="text-center">
                <div class="text-uppercase body-2 my-5">
                    {{ $vuetify.lang.t('$vuetify.login.exclusive-by') }}
                </div>
                <v-img class="align-self-start ml-auto mr-auto"
                       width="auto"
                       height="42.5px"
                       contain
                       src="../../assets/images/knaus_logo.svg"></v-img>
            </div>
        </div>

        <static-content-dialog v-if="validStaticPage"
                               :slug="modal"
                               open></static-content-dialog>

    </v-container>
</template>

<script>
// 3rd
import moment from 'moment';
import {
    mapActions,
    mapState,
} from 'vuex';
// components
import StaticContentDialog from '../../components/StaticContentDialog';
import ErrorList from '../../components/ErrorList';
// models
import User from '../../models/User';
// utils
import {
    evaluatePlatform,
    redirect,
} from '../../utils/helper';
import {errorHandler} from '../../utils/api';
import {
    LOADER_SUBMIT_LOGIN,
    LOADER_TWO_FACTOR,
    LOADER_RESEND_TWO_FACTOR,
    ROUTE_DASHBOARD,
} from '../../utils/constants';
import {
    email,
    password,
} from '../../utils/formRules';
import env from '../../utils/env';

export default {
    name: 'login',
    components: {
        StaticContentDialog,
        ErrorList,
    },
    props: {
        modal: {
            default: null,
        },
    },
    data() {
        return {
            validStaticPage: false,
            showPassword: false,
            valid: true,
            passwordLength: env('password-length'),
            errors: {},
            form: {
                email: '',
                password: '',
                twofactor: '',
            },
            rules: {
                email,
                password,
            },
        };
    },
    computed: {
        ...mapState([
            'showTwoFactor',
            'showLoginForm',
            'countdown',
        ]),
        appstoreLink() {
            return env('appstore-link');
        },
        playstoreLink() {
            return env('playstore-link');
        },
        platform() {
            return evaluatePlatform();
        },
        whileSubmitting() {
            return this.$wait.waiting(LOADER_SUBMIT_LOGIN);
        },
        whileSendingTwoFactor() {
            return this.$wait.waiting(LOADER_RESEND_TWO_FACTOR);
        },
        whileSubmittingTwoFactor() {
            return this.$wait.waiting(LOADER_TWO_FACTOR);
        },
        formattedCountdown() {
            const eventDate = moment(this.countdown);
            const todaysDate = moment();
            return eventDate.diff(todaysDate, 'days');
        },
    },
    methods: {
        ...mapActions([
            'openLoginForm',
            'fetchInit',
            'fetchBootstrap',
            'closeTwoFactorForm',
        ]),
        async submitNewCode() {
            // evaluate agent before login
            const userAgent = evaluatePlatform() === 'web' || evaluatePlatform() === 'pwa' ? 'web' : 'app';
            const loginForm = {
                email: this.form.email,
                password: this.form.password,
                useragent: userAgent,
            }
            if (this.form.twofactor !== '') {
                loginForm.two_factor_code = this.form.twofactor
            }
            try {
                await User.resendTwoFactor(loginForm);
            } catch (e) {
                this.errors = e.response && e.response.data && e.response.data.error ? e.response.data.error : {};
                this.$wait.end(LOADER_RESEND_TWO_FACTOR);
                return;
            }
            this.errors = {
                success: [this.$vuetify.lang.t('$vuetify.validation.new_code_sent')],
            }
        },
        async submit() {
            if (!this.$refs.form.validate()) return false;

            // evaluate agent before login
            const userAgent = evaluatePlatform() === 'web' || evaluatePlatform() === 'pwa' ? 'web' : 'app';
            this.form.userAgent = userAgent;

            const loginForm = {
                email: this.form.email,
                password: this.form.password,
                useragent: userAgent,
            }
            if (this.showTwoFactor) {
                loginForm.two_factor_code = this.form.twofactor
                this.$wait.start(LOADER_TWO_FACTOR);
            }

            try {
                await User.login(loginForm);
            } catch (e) {
                this.errors = e.response && e.response.data && e.response.data.error ? e.response.data.error : {};
                this.$wait.end(LOADER_SUBMIT_LOGIN);
                this.$wait.end(LOADER_TWO_FACTOR);
                return;
            }

            this.errors = {};

            const status = await this.fetchInit().catch(errorHandler);
            await this.fetchBootstrap();


            const redirectTo = localStorage.getItem('redirectTo');
            if (redirectTo) {
                this.$wait.end(LOADER_TWO_FACTOR);
                this.$wait.end(LOADER_SUBMIT_LOGIN);
                localStorage.removeItem('redirectTo');
                await this.$router.push(`/${redirectTo}`);
            } else if (!status.redirected) {
                this.$wait.end(LOADER_TWO_FACTOR);
                this.$wait.end(LOADER_SUBMIT_LOGIN);
                redirect(ROUTE_DASHBOARD);
            }
        },
        resetForm() {
            this.$wait.end(LOADER_TWO_FACTOR);
            this.$wait.end(LOADER_SUBMIT_LOGIN);
            this.form.password = '';
            if (this.$refs && this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },
    },
    watch: {
        showTwoFactor(v) {
            if (!v) {
                this.form.twofactor = ''
                this.errors = {}
            }
        },
        showLoginForm(v) {
            if (!v) {
                this.resetForm();
            }
        },
    },
    mounted() {
        this.$nextTick(() => {
            if (this.modal) {
                const validModals = ['data_protection', 'imprint', 'terms'];
                this.validStaticPage = validModals.indexOf(this.modal) >= 0;
            }

            if (!this.validStaticPage && this.modal === 'show') {
                this.openLoginForm();
            }

            this.resetForm();
        });
    },
};
</script>
